import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { AccountDetailsType, AccountService, ClassroomsService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import {
  Classroom,
  ClassroomParticipant,
  ClassroomParticipantDetailsType,
  FindAccountStrategyEnum,
  IContentBlock,
  IFindAccountsFilter,
  IPagedResults,
  MeetingAcceptStatusEnum,
  User,
} from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const classroomDetails: ClassroomParticipantDetailsType[] = ['id', 'user_id', 'accept_status', 'joined_at', 'user:sm'];
const usersDetails: AccountDetailsType[] = ['id', 'first_name', 'last_name', 'country', 'userpic:sm'];
const pagination = { page: 1, pageSize: 100, totalPages: 0, total: 0 };

type UsersBlockType = IContentBlock<User, IFindAccountsFilter>;
type UsersLoadingMode = 'my' | 'all';

@Component({
  selector: 'app-invite-to-classroom',
  templateUrl: './invite-to-classroom.component.html',
  styleUrls: ['./invite-to-classroom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteToClassroomComponent extends ChangableComponent implements OnInit, OnChanges {
  deviceService = inject(DeviceDetectorService);
  private classroomsService = inject(ClassroomsService);
  private accountService = inject(AccountService);

  @Input() classroom: Classroom;
  @Output() invited = new EventEmitter<ClassroomParticipant[]>();

  public users: UsersBlockType = {
    items: [],
    loading: false,
    loaded: false,
    filter: {},
    pagination: {
      page: 1,
      pageSize: 100,
      total: 0,
      totalPages: 0,
    },
  };
  public paginationTimeout: Subscription;
  public paginationEnabled = false;

  private mode: UsersLoadingMode;

  ngOnInit() {
    this.mode = 'my';
    this.loadUsers();
  }

  ngOnChanges(): void {
    this.getParticipantsStatus();
  }

  public onSearchChanged(term: string): void {
    if (term && term.length > 1) {
      this.mode = 'all';
      this.users.filter = { term };
      this.loadUsers();
    } else {
      this.mode = 'my';
      this.users.filter = {};
      this.loadUsers(false);
    }
  }

  public onCloseSearch(): void {
    this.mode = 'my';
    this.users.filter = {};
    this.loadUsers(false);
  }

  public nextPage() {
    if (this.paginationEnabled) {
      this.loadUsers(true);
    }
  }

  public loadUsers(nextPage?: boolean) {
    if (this.users.loading) {
      return;
    }
    this.users.loading = true;

    if (nextPage && this.users.pagination.page < this.users.pagination.totalPages) {
      this.users.pagination.page = this.users.pagination.page + 1;
    }

    let usersLoader: Observable<IPagedResults<User[]>>;

    switch (this.mode) {
      case 'my':
        usersLoader = this.accountService.findAccounts(
          { use: FindAccountStrategyEnum.my_students },
          this.users.pagination,
          usersDetails,
        );
        break;

      case 'all':
        usersLoader = this.accountService.findAccounts(this.users.filter, this.users.pagination, usersDetails);
    }

    usersLoader.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (response) => {
        if (nextPage) {
          response.results.map((t) => {
            if (!this.users.items.find((t1) => t1.id === t.id)) {
              this.users.items.push(t);
            }
          });
        } else {
          this.users.items = response.results;
        }

        if (response.pagination) {
          this.users.pagination = response.pagination;
        } else {
          this.users.pagination.total = response.total;
          this.users.pagination.totalPages = response.totalPages;
          this.users.pagination.page = response.page;
        }

        this.paginationEnabled = false;
        this.users.loaded = true;
        this.users.loading = false;

        this.paginationTimeout = this.setTimeout(() => {
          this.paginationEnabled = true;
          this.markForCheck();
        }, 1500);

        this.markForCheck();
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        this.users.loading = false;
        this.markForCheck();
      },
    });
  }

  public getParticipantsStatus(): void {
    this.classroomsService
      .getClassroomParticipants(this.classroom.id, pagination, classroomDetails, {})
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (participants) => {
          this.classroom = {
            ...this.classroom,
            participants: participants.results,
          };
          this.markForCheck();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public invite(participants: ClassroomParticipant[] = [], emails: string[] = []) {
    if (participants.length > 0 || emails.length > 0) {
      this.classroomsService
        .inviteUsers(this.classroom.id, participants, emails, classroomDetails)
        .pipe(takeUntil(this.destroyed$))
        .subscribe({
          next: () => {
            this.getParticipantsStatus();
          },
          error: (error) => {
            this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          },
        });
    }
  }

  public isUserInvited(user: User): boolean {
    return this.classroom.participants.some(
      (x) => x.user_id === user.id && x.accept_status === MeetingAcceptStatusEnum.required,
    );
  }

  public isUserAccepted(user: User): boolean {
    return this.classroom.participants.some(
      (x) => x.user_id === user.id && x.accept_status === MeetingAcceptStatusEnum.accepted,
    );
  }

  public onInvite(user: User): void {
    const participant = new ClassroomParticipant({ user_id: user.id });
    this.invite([participant]);
  }

  public trackByFn(_: number, user: User): string {
    return user.id;
  }
}
